/**
 * New-game wizard: league → conference → team → fixture.
 *
 * Picking a real fixture is the point. The game arrives with both teams named,
 * their colors and logos set, the venue filled in and kickoff dated — instead
 * of "Home vs Away" and a form to fill in by hand.
 *
 * Only the event id and team id go to the server; it re-resolves the matchup
 * from ESPN and our own library, so the browser never dictates what the teams
 * are.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, toTeamEntry } from '../lib/api';
import type { TeamRow } from '../lib/api';
import { onColor, readableTeamColor } from '../../shared/team-colors';
import { fixtureTiming, splitFixtures } from '../../shared/fixtures';
import { isCurrentSeason } from '../../shared/season';
import { LEAGUES, LEAGUE_ORDER, sportOf } from '../../shared/sports';
import type { Fixture, GroupNode, League, TeamLibraryEntry } from '../../shared/types';

type Step = 'league' | 'conference' | 'team' | 'fixture';

interface Props {
  onCreated: (gameId: string) => void;
  onManual: () => void;
  onClose: () => void;
}

export function NewGameWizard({ onCreated, onManual, onClose }: Props) {
  const [step, setStep] = useState<Step>('league');
  const [league, setLeague] = useState<League | null>(null);
  const [conference, setConference] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamLibraryEntry | null>(null);

  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [season, setSeason] = useState<number | null>(null);
  /** True once the regular season is spent and the bracket has been consulted. */
  const [postseasonChecked, setPostseasonChecked] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [showPlayed, setShowPlayed] = useState(false);

  const run = useCallback(async <T,>(work: () => Promise<T>, apply: (value: T) => void) => {
    setLoading(true);
    setError(null);
    try {
      apply(await work());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, []);

  const chooseLeague = (next: League) => {
    setLeague(next);
    setStep('conference');
    void run(
      () => api.groups(next),
      (res) => setGroups(res.groups),
    );
  };

  const chooseConference = (next: string) => {
    setConference(next);
    setStep('team');
    void run(
      () => api.searchTeams({ league: league!, conference: next, limit: '200' }),
      (res) => setTeams(res.teams),
    );
  };

  const loadSchedule = useCallback(
    (teamId: string) =>
      run(
        () => api.schedule(teamId),
        (res) => {
          setFixtures(res.fixtures);
          setSeason(res.season);
          setPostseasonChecked(res.postseasonChecked ?? false);
        },
      ),
    [run],
  );

  const chooseTeam = (row: TeamRow) => {
    setTeam(toTeamEntry(row));
    setFixtures([]);
    setShowPlayed(false);
    setStep('fixture');
    void loadSchedule(row.id);
  };

  const chooseFixture = async (fixture: Fixture) => {
    if (!team) return;
    setCreating(fixture.eventId);
    setError(null);
    try {
      const game = await api.createGame({
        from: { teamId: team.id, eventId: fixture.eventId },
      });
      onCreated(game.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the game.');
      setCreating(null);
    }
  };

  // Finished games are separated out rather than filtered away, so the list
  // still looks like this team's season.
  const { selectable, played } = splitFixtures(fixtures);

  /**
   * Why a team with a full schedule has nothing to offer.
   *
   * These are genuinely different situations and used to share one message.
   * A team out of regular-season games in December is usually waiting on a
   * bowl or a playoff seed, which will appear here on its own; a team out of
   * games in May has finished its season entirely and the next one is not
   * published. Telling someone to "pick a later season" in the first case
   * sends them looking for something that is already on its way.
   */
  const teamName = team?.name ?? 'This team';
  const noGamesLeft = !isCurrentSeason(season)
    ? `The ${season ?? 'last'} season is over. ${teamName}'s next schedule appears` +
      ' here once ESPN publishes it, usually in the spring.'
    : postseasonChecked
      ? `${teamName} has finished the regular season. Any bowl or playoff game` +
        ' shows up here once the matchup is announced.'
      : `No games left on ${teamName}'s schedule.`;

  const back = () => {
    setError(null);
    if (step === 'fixture') return setStep('team');
    if (step === 'team') return setStep('conference');
    if (step === 'conference') return setStep('league');
  };

  // Escape closes, matching every other modal in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="New game"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal wizard">
        <div className="modal-head">
          <div className="row" style={{ gap: 8 }}>
            {step !== 'league' && (
              <button type="button" className="btn-ghost" onClick={back} aria-label="Back">
                ←
              </button>
            )}
            <h2>{headingFor(step, league, conference, team)}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <ol className="wizard-steps" aria-label="Progress">
          {(['league', 'conference', 'team', 'fixture'] as Step[]).map((s, i) => (
            <li
              key={s}
              className={
                s === step ? 'current' : stepIndex(step) > i ? 'done' : ''
              }
            >
              {['League', 'Conference', 'Team', 'Game'][i]}
            </li>
          ))}
        </ol>

        {error && (
          <p className="small" role="alert" style={{ color: 'var(--danger)', marginBottom: 12 }}>
            {error}
          </p>
        )}
        {loading && <p className="small muted">Loading…</p>}

        {step === 'league' && (
          <div className="stack" style={{ gap: 10 }}>
            {LEAGUE_ORDER.map((id) => {
              const meta = LEAGUES[id];
              return (
                <button
                  key={id}
                  type="button"
                  className="wizard-tile"
                  onClick={() => chooseLeague(id)}
                >
                  <strong>{meta.label}</strong>
                  <span className="small muted">
                    {meta.description}
                    {/* Said up front, because it changes how the pot is split
                        and people expect four payouts. */}
                    {meta.periodCount !== 4 &&
                      ` · ${meta.periodCount} halves, ${meta.periodCount} payouts`}
                  </span>
                </button>
              );
            })}
            <button type="button" className="btn-ghost small" onClick={onManual}>
              Skip — set the matchup up by hand
            </button>
          </div>
        )}

        {step === 'conference' && !loading && (
          <div className="wizard-grid">
            {conferencesOf(groups).map((c) => (
              <button
                key={c.conference}
                type="button"
                className="wizard-tile"
                onClick={() => chooseConference(c.conference)}
              >
                <strong>{c.conference}</strong>
                <span className="small muted">{c.teamCount} teams</span>
              </button>
            ))}
          </div>
        )}

        {step === 'team' && !loading && (
          <div className="stack" style={{ gap: 4 }}>
            {groupByDivision(teams).map(([division, rows]) => (
              <div key={division || 'all'}>
                {division && <h3 className="card-title" style={{ marginTop: 12 }}>{division}</h3>}
                {rows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className="wizard-row"
                    onClick={() => chooseTeam(row)}
                  >
                    <TeamMark
                      logoUrl={row.logo_url}
                      abbr={row.abbr}
                      color={row.primary_color}
                      size={30}
                    />
                    <span>
                      <strong>{row.name}</strong>{' '}
                      <span className="muted small">{row.mascot}</span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {step === 'fixture' && !loading && error && (
          <div className="empty-state">
            <p className="small muted" style={{ marginBottom: 12 }}>
              Schedules come from ESPN, which occasionally rate-limits us. Nothing
              is wrong with your game.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => team && void loadSchedule(team.id)}
            >
              Try again
            </button>
            <p className="small muted" style={{ marginTop: 14 }}>
              or{' '}
              <button
                type="button"
                className="btn-ghost small"
                style={{ minHeight: 'auto', padding: 2 }}
                onClick={onManual}
              >
                set the matchup up by hand
              </button>
            </p>
          </div>
        )}

        {step === 'fixture' && !loading && !error && (
          <>
            {fixtures.length === 0 ? (
              <p className="empty-state">
                No schedule published for {team?.name} yet.
              </p>
            ) : selectable.length === 0 ? (
              <p className="empty-state">{noGamesLeft}</p>
            ) : (
              <div className="stack" style={{ gap: 4 }}>
                {season && (
                  <p className="small muted" style={{ marginBottom: 4 }}>
                    {season} season · tap a game to build the board
                  </p>
                )}
                {selectable.map((fixture) => (
                  <FixtureRow
                    key={fixture.eventId}
                    fixture={fixture}
                    busy={creating === fixture.eventId}
                    disabled={creating !== null}
                    onSelect={() => void chooseFixture(fixture)}
                  />
                ))}
              </div>
            )}

            {/* Kept visible, but never selectable: a board cannot be sold and
                drawn after the final whistle. They are here so the list still
                reads as the right team's season. */}
            {played.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn-ghost small"
                  aria-expanded={showPlayed}
                  onClick={() => setShowPlayed((v) => !v)}
                >
                  {showPlayed ? 'Hide' : 'Show'} {played.length} already played
                </button>
                {showPlayed && (
                  <div className="stack" style={{ gap: 4, marginTop: 6 }}>
                    {played.map((fixture) => (
                      <FixtureRow key={fixture.eventId} fixture={fixture} played />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FixtureRow({
  fixture,
  busy = false,
  disabled = false,
  played = false,
  onSelect,
}: {
  fixture: Fixture;
  busy?: boolean;
  disabled?: boolean;
  /** Already finished: shown for context, never selectable. */
  played?: boolean;
  onSelect?: () => void;
}) {
  const timing = fixtureTiming(fixture);
  return (
    <button
      type="button"
      className={played ? 'wizard-row is-played' : 'wizard-row'}
      onClick={onSelect}
      disabled={disabled || played}
      aria-label={
        played
          ? `${fixture.name}, already played`
          : `${fixture.name}, ${formatKickoff(fixture.kickoffAt)}`
      }
    >
      <TeamMark
        logoUrl={fixture.away.logoUrl}
        abbr={fixture.away.abbr}
        color={fixture.away.color}
        size={26}
      />
      <span className="small muted">@</span>
      <TeamMark
        logoUrl={fixture.home.logoUrl}
        abbr={fixture.home.abbr}
        color={fixture.home.color}
        size={26}
      />
      <span style={{ minWidth: 0, flex: 1 }}>
        <strong style={{ display: 'block' }}>
          {fixture.away.name} at {fixture.home.name}
        </strong>
        <span className="small muted">
          {formatKickoff(fixture.kickoffAt)}
          {fixture.week ? ` · ${fixture.week}` : ''}
          {played && ' · final'}
          {timing === 'started' && !played && ' · under way'}
        </span>
      </span>
      {busy && <span className="small muted">Creating…</span>}
    </button>
  );
}

/** Logo when we have one, otherwise a colored monogram. */
function TeamMark({
  logoUrl,
  abbr,
  color,
  size,
}: {
  logoUrl: string;
  abbr: string;
  color: string;
  size: number;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }
  const fill = readableTeamColor(color);
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 6,
        fontSize: size * 0.36,
        fontWeight: 700,
        background: fill,
        color: onColor(fill),
      }}
    >
      {abbr.slice(0, 3)}
    </span>
  );
}

function stepIndex(step: Step): number {
  return ['league', 'conference', 'team', 'fixture'].indexOf(step);
}

function headingFor(
  step: Step,
  league: League | null,
  conference: string | null,
  team: TeamLibraryEntry | null,
): string {
  switch (step) {
    case 'league':
      return 'Pick a league';
    case 'conference':
      return 'Pick a conference';
    case 'team':
      return conference ? `${conference} teams` : 'Pick a team';
    case 'fixture':
      return team ? `${team.name} schedule` : 'Pick a game';
  }
}

/** Distinct conferences, preserving the server's ordering. */
function conferencesOf(groups: GroupNode[]): GroupNode[] {
  const merged = new Map<string, GroupNode>();
  for (const g of groups) {
    const existing = merged.get(g.conference);
    if (existing) existing.teamCount += g.teamCount;
    else merged.set(g.conference, { ...g });
  }
  return [...merged.values()];
}

/**
 * Buckets teams by division so the NFL list reads AFC East / North / …
 * College conferences have no divisions and fall into a single unlabelled
 * bucket.
 */
function groupByDivision(rows: TeamRow[]): [string, TeamRow[]][] {
  const buckets = new Map<string, TeamRow[]>();
  for (const row of rows) {
    const key = row.division ?? '';
    const list = buckets.get(key);
    if (list) list.push(row);
    else buckets.set(key, [row]);
  }
  return [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function formatKickoff(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
